'use client';

import type { ChartConfig } from '../../ui';
import type { ChartSummaryTooltipLabels } from './chart-summary-tooltip';

import { cn } from '@polyseme/utils/cn/cn-utils';
import { ObjectUtils } from '@polyseme/utils/typescript/typescript-utils';
import {
  Collapsible,
  CollapsibleTrigger,
  CollapsibleContent,
} from '@radix-ui/react-collapsible';
import { Suspense, useState } from 'react';

import { Icon } from '../icons';
import { Typography } from '../typography';
import { ChartAreaAxes } from './chart-area-axes';
import { ChartAreaAxesSkeleton } from './chart-area-axes-skeleton';

export type YearProjection = {
  investmentMade: number;
  median: number;
  optimistic: number;
  pessimistic: number;
  year: number;
};

type ColorConfig = {
  dotBg: string;
  textColor: string;
  lineColor: string;
};

type Explanation = {
  subtitle: string;
  sentence: string;
};

type ConfigItem = {
  colors: ColorConfig;
  legend: string;
  explanation?: Explanation;
};

type Config = {
  [k in string]: ConfigItem;
};

export type ProjectionVariant = 'default' | 'floating';

type ProjectionChartProps = {
  blurAmounts?: boolean;
  explanationsTitle?: string;
  chartConfig: ChartConfig;
  /** Override the chart plotting area sizing. @see ChartAreaAxes */
  chartContainerClassName?: string;
  config: Config;
  collapsible?: boolean;
  maxAmount?: number;
  /** Hide the summary tooltip card above the chart. @default true */
  showSummary?: boolean;
  /** Hide the legend row below the chart. @default true */
  showLegend?: boolean;
  /** Hide the explanations collapsible card. @default true */
  showExplanations?: boolean;
  variant?: ProjectionVariant;
  projections: YearProjection[];
  tooltipLabels: ChartSummaryTooltipLabels;
  /** Fired with the year currently hovered (or the last year on mouse-leave). */
  onActiveYearChange?: (year: number) => void;
};

export const ProjectionChart = ({
  blurAmounts = false,
  chartConfig,
  chartContainerClassName,
  collapsible = false,
  config,
  explanationsTitle,
  maxAmount,
  onActiveYearChange,
  showSummary = true,
  showLegend = true,
  showExplanations = true,
  variant = 'default',
  projections,
  tooltipLabels,
}: ProjectionChartProps) => {
  const [isExplanationOpen, setIsExplanationOpen] = useState(!collapsible);

  const specs = ObjectUtils.keys(config).map((key) => {
    const item = config[key];
    if (!item) {
      return {
        colors: { dotBg: '', textColor: '', lineColor: '' },
        legend: '',
        explanation: undefined,
      };
    }
    return {
      colors: item.colors,
      legend: item.legend,
      explanation: item.explanation,
    };
  }) satisfies ConfigItem[];

  return (
    <div className="relative flex w-full flex-col gap-4">
      <Suspense
        fallback={<ChartAreaAxesSkeleton className={chartContainerClassName} />}
      >
        {projections.length ? (
          <ChartAreaAxes
            blurAmounts={blurAmounts}
            chartConfig={chartConfig}
            chartContainerClassName={chartContainerClassName}
            chartData={projections}
            maxAmount={maxAmount}
            onActiveYearChange={onActiveYearChange}
            showSummary={showSummary}
            tooltipLabels={tooltipLabels}
            variant={variant}
          />
        ) : (
          <ChartAreaAxesSkeleton className={chartContainerClassName} />
        )}
      </Suspense>

      {!!showLegend && (
        <div className="flex w-full flex-wrap items-start gap-4 text-sm">
          {specs.map(({ colors, legend }, index) => (
            <div key={`legend-${index}`} className="flex items-center gap-2">
              <div className={`size-2 shrink-0 rounded-sm ${colors.dotBg}`} />
              <Typography className="text-foreground" variant="caption">
                {legend}
              </Typography>
            </div>
          ))}
        </div>
      )}

      {!!showExplanations && (
        <Collapsible
          className="rounded-md border border-gray-200 bg-gray-50 p-6 transition-colors"
          open={isExplanationOpen}
          onOpenChange={collapsible ? setIsExplanationOpen : undefined}
        >
          <CollapsibleTrigger
            className={cn('flex w-full items-center justify-between', {
              'cursor-auto': !collapsible,
            })}
          >
            <Typography variant="title-6">{explanationsTitle}</Typography>
            {!!collapsible && (
              <Icon
                className={`size-4 transition-transform ${isExplanationOpen ? 'rotate-180' : ''}`}
                name="chevron-down"
              />
            )}
          </CollapsibleTrigger>

          <CollapsibleContent className="mt-3 space-y-3 text-sm text-gray-700">
            {specs.map(({ explanation, colors }, index) => (
              <div
                key={`collapsible-item-${index}`}
                className="flex items-start gap-3"
              >
                <div
                  className={`mt-2 size-2 shrink-0 -translate-y-0.5 rounded-sm ${colors.dotBg}`}
                />

                {!!explanation && (
                  <Typography>
                    <Typography
                      as="span"
                      className="font-medium text-foreground"
                    >
                      {explanation.subtitle}
                    </Typography>
                    &nbsp;{explanation.sentence}
                  </Typography>
                )}
              </div>
            ))}
          </CollapsibleContent>
        </Collapsible>
      )}
    </div>
  );
};
